/**
 * Findings list — groups a list of findings by section, sorted by severity.
 */

import { useMemo } from 'react'
import type { MergedReviewFinding } from '@/lib/schemas'
import { FindingCard } from '@/components/drafting/FindingCard'

interface FindingsListProps {
  artifactId: string
  findings: MergedReviewFinding[]
  canFixSection: (sectionId: string) => boolean
  sectionTitleFor?: (sectionId: string | null) => string | null
  onFix?: (finding: MergedReviewFinding) => void
  canPromoteToComment?: boolean
  onPromoteToComment?: (finding: MergedReviewFinding) => void
  promotedKeys?: Set<string>
}

const severityOrder: Record<MergedReviewFinding['severity'], number> = {
  high: 0,
  medium: 1,
  low: 2,
}

export function FindingsList({
  artifactId,
  findings,
  canFixSection,
  sectionTitleFor,
  onFix,
  canPromoteToComment,
  onPromoteToComment,
  promotedKeys,
}: FindingsListProps) {
  const groups = useMemo(() => groupBySection(findings), [findings])

  if (findings.length === 0) return null

  return (
    <div className="space-y-4">
      {groups.map(({ sectionId, items }) => (
        <div key={sectionId ?? '__general__'} className="space-y-2">
          <div className="flex min-w-0 items-center gap-1 text-xs font-medium text-muted-foreground">
            <span className="min-w-0 truncate">
              {sectionId === null ? 'General' : (sectionTitleFor?.(sectionId) ?? sectionId)}
            </span>
            <span className="shrink-0 whitespace-nowrap font-normal">
              · {items.length} {items.length === 1 ? 'finding' : 'findings'}
            </span>
          </div>
          <ul className="space-y-2">
            {items.map((f) => (
              <li key={f.issueKey}>
                <FindingCard
                  artifactId={artifactId}
                  finding={f}
                  canFix={f.sectionId ? canFixSection(f.sectionId) : false}
                  onFix={onFix}
                  canPromoteToComment={canPromoteToComment}
                  onPromoteToComment={onPromoteToComment}
                  alreadyPromoted={promotedKeys?.has(f.issueKey)}
                />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}

function groupBySection(findings: MergedReviewFinding[]) {
  const map = new Map<string | null, MergedReviewFinding[]>()
  for (const f of findings) {
    const key = f.sectionId
    const bucket = map.get(key) ?? []
    bucket.push(f)
    map.set(key, bucket)
  }
  for (const items of map.values()) {
    items.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity])
  }
  const groups = Array.from(map.entries()).map(([sectionId, items]) => ({ sectionId, items }))
  // Sections first, General last.
  groups.sort((a, b) => {
    if (a.sectionId === null) return 1
    if (b.sectionId === null) return -1
    return 0
  })
  return groups
}
